import { useState } from 'react';
import { motion } from 'framer-motion';
import { Play, Radio } from 'lucide-react';
import { ShortsHighlights } from '../components/ShortsHighlights';

const YOUTUBE_CHANNEL_ID = 'UCdJtc_ATtcQV6BKByvucFQA';

export const Media = () => {
  const [showLive, setShowLive] = useState(true);

  return (
    <div data-testid="media-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-gold uppercase tracking-[0.3em] text-sm mb-4">Watch & Listen</p>
            <h1 className="font-cinzel text-4xl sm:text-5xl lg:text-6xl text-foreground mb-6">
              Media <span className="text-gold">Center</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Catch up on past messages, watch live streams, and dive deeper into the Word with our video library.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Live Stream Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* Toggle Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <button
                onClick={() => setShowLive(true)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
                  showLive ? 'bg-red-600/20 text-red-500' : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
                data-testid="toggle-live"
              >
                <Radio size={16} className={showLive ? 'animate-pulse' : ''} />
                <span className="text-sm font-semibold uppercase tracking-wider">Live Stream</span>
              </button>
              <button
                onClick={() => setShowLive(false)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
                  !showLive ? 'bg-gold/20 text-gold' : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
                data-testid="toggle-recent"
              >
                <Play size={16} />
                <span className="text-sm font-semibold uppercase tracking-wider">Recent Videos</span>
              </button>
            </div>

            <div 
              className="relative aspect-video bg-black border border-border rounded-sm overflow-hidden gold-glow"
              data-testid="live-stream-container"
            >
              {showLive ? (
                <iframe
                  src={`https://www.youtube.com/embed/live_stream?channel=${YOUTUBE_CHANNEL_ID}&autoplay=0`}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="True Believer Live Stream"
                />
              ) : (
                <iframe
                  src="https://www.youtube.com/embed/videoseries?list=PLKZh3_JqvilPwY-tZL9Z5FsmWH_BaWUeQ"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="True Believer Recent Videos"
                />
              )}
            </div>

            <div className="mt-4 p-4 bg-muted/50 border border-border rounded-sm">
              <p className="text-sm text-muted-foreground">
                {showLive ? (
                  <>
                    <strong className="text-gold">Live Stream:</strong> When we go live on YouTube, the stream appears here automatically. 
                    Not live? Switch to <button onClick={() => setShowLive(false)} className="text-gold underline">Recent Videos</button> to catch up on past messages.
                  </>
                ) : (
                  <>
                    <strong className="text-gold">Recent Videos:</strong> Browse our latest messages and teachings. 
                    Want to watch live? Switch to <button onClick={() => setShowLive(true)} className="text-gold underline">Live Stream</button> during service times.
                  </>
                )}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <ShortsHighlights />

      {/* Subscribe CTA */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-cinzel text-3xl text-foreground mb-4">
              Never Miss a <span className="text-gold">Message</span>
            </h2>
            <p className="text-muted-foreground mb-8">
              Subscribe to our YouTube channel and follow us on social media to get notified when we go live.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href={`https://www.youtube.com/channel/${YOUTUBE_CHANNEL_ID}?sub_confirmation=1`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
                data-testid="subscribe-youtube"
              >
                Subscribe on YouTube
              </a>
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
              >
                Follow on Facebook
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
